/**
 * MinimaxEngine - Alpha-Beta Search with Iterative Deepening
 *
 * Core search algorithm for finding optimal moves. Uses:
 * - Alpha-beta pruning for efficient tree search
 * - Iterative deepening for time management
 * - Move ordering for better pruning
 * - Quiescence search to avoid horizon effects
 */

import { GameMap } from '../../../Map';
import {
  GameStateSnapshot,
  UnitSnapshot,
  GeneratedMove,
  SearchConfig,
  getSearchConfig
} from '../V3Types';
import {
  applyMove,
  switchPerspective,
  getActiveUnits
} from './GameStateSnapshot';
import {
  generateMovesForUnit,
  orderMoves,
  limitMoves
} from './MoveGenerator';
import { evaluate, quickEvaluate } from './Evaluator';
import { TranspositionTable, EntryType } from './TranspositionTable';

// Search result containing best move and its score
export interface SearchResult {
  move: GeneratedMove | null;
  score: number;
  depth: number;
  nodesSearched: number;
  timeMs: number;
}

// Internal search state
interface SearchState {
  nodesSearched: number;
  startTime: number;
  timeLimit: number;
  transpositionTable: TranspositionTable;
  killerMoves: Map<number, GeneratedMove[]>;  // Depth -> killer moves
  historyTable: Map<string, number>;  // Move key -> success count
  abortSearch: boolean;
}

/**
 * Main search function - finds the best move for the current position
 */
export function findBestMove(
  snapshot: GameStateSnapshot,
  map: GameMap,
  config?: Partial<SearchConfig>
): SearchResult {
  const activeUnits = getActiveUnits(snapshot, snapshot.currentFaction);
  const defaultConfig = getSearchConfig(activeUnits.length);
  const searchConfig: SearchConfig = { ...defaultConfig, ...config };

  const state: SearchState = {
    nodesSearched: 0,
    startTime: Date.now(),
    timeLimit: searchConfig.timeLimit,
    transpositionTable: new TranspositionTable(),
    killerMoves: new Map(),
    historyTable: new Map(),
    abortSearch: false
  };

  let bestResult: SearchResult = {
    move: null,
    score: -Infinity,
    depth: 0,
    nodesSearched: 0,
    timeMs: 0
  };

  // Iterative deepening - search deeper until time runs out
  for (let depth = 1; depth <= searchConfig.maxDepth; depth++) {
    if (isTimeUp(state)) break;

    const result = searchRoot(snapshot, map, depth, state, searchConfig);

    if (!state.abortSearch && result.move) {
      bestResult = result;
      bestResult.depth = depth;

      // If we found a winning move, stop searching
      if (result.score > 50000) {
        break;
      }
    }

    // Check if we have enough time for another iteration
    const elapsed = Date.now() - state.startTime;
    const estimatedNextIteration = elapsed * 4; // Rough estimate: each depth takes 4x longer
    if (estimatedNextIteration > state.timeLimit) {
      break;
    }
  }

  bestResult.nodesSearched = state.nodesSearched;
  bestResult.timeMs = Date.now() - state.startTime;

  return bestResult;
}

/**
 * Find the best move for a SPECIFIC unit
 * This ensures each unit gets planned individually, not competing with other units
 */
export function findBestMoveForUnit(
  snapshot: GameStateSnapshot,
  map: GameMap,
  unitId: string,
  config?: Partial<SearchConfig>
): SearchResult {
  const activeUnits = getActiveUnits(snapshot, snapshot.currentFaction);
  const unit = activeUnits.find(u => u.id === unitId);

  if (!unit) {
    return {
      move: null,
      score: 0,
      depth: 0,
      nodesSearched: 0,
      timeMs: 0
    };
  }

  const defaultConfig = getSearchConfig(activeUnits.length);
  const searchConfig: SearchConfig = { ...defaultConfig, ...config };

  const state: SearchState = {
    nodesSearched: 0,
    startTime: Date.now(),
    timeLimit: searchConfig.timeLimit,
    transpositionTable: new TranspositionTable(),
    killerMoves: new Map(),
    historyTable: new Map(),
    abortSearch: false
  };

  let bestResult: SearchResult = {
    move: null,
    score: -Infinity,
    depth: 0,
    nodesSearched: 0,
    timeMs: 0
  };

  // Iterative deepening - search deeper until time runs out
  for (let depth = 1; depth <= searchConfig.maxDepth; depth++) {
    if (isTimeUp(state)) break;

    const result = searchRootForUnit(snapshot, map, unit, depth, state, searchConfig);

    if (!state.abortSearch && result.move) {
      bestResult = result;
      bestResult.depth = depth;

      // If we found a winning move, stop searching
      if (result.score > 50000) {
        break;
      }
    }

    // Check if we have enough time for another iteration
    const elapsed = Date.now() - state.startTime;
    const estimatedNextIteration = elapsed * 4; // Rough estimate: each depth takes 4x longer
    if (estimatedNextIteration > state.timeLimit) {
      break;
    }
  }

  bestResult.nodesSearched = state.nodesSearched;
  bestResult.timeMs = Date.now() - state.startTime;

  return bestResult;
}

/**
 * Root search - generates all moves and finds the best one
 */
function searchRoot(
  snapshot: GameStateSnapshot,
  map: GameMap,
  depth: number,
  state: SearchState,
  config: SearchConfig
): SearchResult {
  const activeUnits = getActiveUnits(snapshot, snapshot.currentFaction);
  let allMoves: GeneratedMove[] = [];

  // Generate moves for all active units
  for (const unit of activeUnits) {
    const unitMoves = generateMovesForUnit(snapshot, unit, map);
    allMoves.push(...unitMoves);
  }

  // Order moves for better pruning
  allMoves = orderMoves(allMoves);

  // Apply history heuristic
  allMoves = applyHistoryOrdering(allMoves, state);

  // Limit moves per unit for performance
  allMoves = limitMoves(allMoves, config.maxMovesPerUnit * activeUnits.length);

  if (allMoves.length === 0) {
    return {
      move: null,
      score: evaluate(snapshot, map),
      depth,
      nodesSearched: state.nodesSearched,
      timeMs: Date.now() - state.startTime
    };
  }

  let bestMove: GeneratedMove | null = null;
  let bestScore = -Infinity;
  let alpha = -Infinity;
  const beta = Infinity;

  for (const move of allMoves) {
    if (isTimeUp(state)) {
      state.abortSearch = true;
      break;
    }

    const newSnapshot = applyMove(snapshot, move);
    const score = -alphaBeta(
      switchPerspective(newSnapshot),
      map,
      depth - 1,
      -beta,
      -alpha,
      state,
      config
    );

    if (score > bestScore) {
      bestScore = score;
      bestMove = move;
    }

    if (score > alpha) {
      alpha = score;
    }
  }

  // Update history table for successful move
  if (bestMove) {
    updateHistory(bestMove, depth, state);
  }

  return {
    move: bestMove,
    score: bestScore,
    depth,
    nodesSearched: state.nodesSearched,
    timeMs: Date.now() - state.startTime
  };
}

/**
 * Root search for a SPECIFIC unit - only generates moves for that unit
 * This ensures each unit gets properly planned without other units' moves interfering
 */
function searchRootForUnit(
  snapshot: GameStateSnapshot,
  map: GameMap,
  unit: UnitSnapshot,
  depth: number,
  state: SearchState,
  config: SearchConfig
): SearchResult {
  // Generate moves for ONLY this unit
  let moves = generateMovesForUnit(snapshot, unit, map);

  // Order moves for better pruning
  moves = orderMoves(moves);

  // Apply history heuristic
  moves = applyHistoryOrdering(moves, state);

  // Limit moves for performance
  moves = limitMoves(moves, config.maxMovesPerUnit);

  if (moves.length === 0) {
    return {
      move: null,
      score: evaluate(snapshot, map),
      depth,
      nodesSearched: state.nodesSearched,
      timeMs: Date.now() - state.startTime
    };
  }

  let bestMove: GeneratedMove | null = null;
  let bestScore = -Infinity;
  let alpha = -Infinity;
  const beta = Infinity;

  for (const move of moves) {
    if (isTimeUp(state)) {
      state.abortSearch = true;
      break;
    }

    const newSnapshot = applyMove(snapshot, move);
    const score = -alphaBeta(
      switchPerspective(newSnapshot),
      map,
      depth - 1,
      -beta,
      -alpha,
      state,
      config
    );

    if (score > bestScore) {
      bestScore = score;
      bestMove = move;
    }

    if (score > alpha) {
      alpha = score;
    }
  }

  // Update history table for successful move
  if (bestMove) {
    updateHistory(bestMove, depth, state);
  }

  return {
    move: bestMove,
    score: bestScore,
    depth,
    nodesSearched: state.nodesSearched,
    timeMs: Date.now() - state.startTime
  };
}

/**
 * Alpha-Beta search with negamax formulation
 */
function alphaBeta(
  snapshot: GameStateSnapshot,
  map: GameMap,
  depth: number,
  alpha: number,
  beta: number,
  state: SearchState,
  config: SearchConfig
): number {
  state.nodesSearched++;

  // Check transposition table
  const ttEntry = state.transpositionTable.lookup(snapshot);
  if (ttEntry && ttEntry.depth >= depth) {
    if (ttEntry.type === EntryType.EXACT) {
      return ttEntry.score;
    } else if (ttEntry.type === EntryType.LOWER_BOUND) {
      alpha = Math.max(alpha, ttEntry.score);
    } else if (ttEntry.type === EntryType.UPPER_BOUND) {
      beta = Math.min(beta, ttEntry.score);
    }

    if (alpha >= beta) {
      return ttEntry.score;
    }
  }

  // Terminal or depth limit
  if (depth <= 0) {
    // Quiescence search for volatile positions
    return quiescenceSearch(snapshot, map, alpha, beta, state, 4);
  }

  // Time check
  if (isTimeUp(state)) {
    state.abortSearch = true;
    return evaluate(snapshot, map);
  }

  const activeUnits = getActiveUnits(snapshot, snapshot.currentFaction);

  // Check for no moves (pass turn)
  if (activeUnits.length === 0) {
    // Switch perspective and continue
    return -alphaBeta(
      switchPerspective(snapshot),
      map,
      depth - 1,
      -beta,
      -alpha,
      state,
      config
    );
  }

  let allMoves: GeneratedMove[] = [];
  for (const unit of activeUnits) {
    const unitMoves = generateMovesForUnit(snapshot, unit, map);
    allMoves.push(...unitMoves);
  }

  // Order moves
  allMoves = orderMoves(allMoves);
  allMoves = applyKillerMoveOrdering(allMoves, depth, state);
  allMoves = limitMoves(allMoves, config.maxMovesPerUnit * Math.max(1, activeUnits.length));

  if (allMoves.length === 0) {
    return evaluate(snapshot, map);
  }

  let bestScore = -Infinity;
  let bestMove: GeneratedMove | null = null;
  let entryType = EntryType.UPPER_BOUND;

  for (const move of allMoves) {
    const newSnapshot = applyMove(snapshot, move);
    const score = -alphaBeta(
      switchPerspective(newSnapshot),
      map,
      depth - 1,
      -beta,
      -alpha,
      state,
      config
    );

    if (score > bestScore) {
      bestScore = score;
      bestMove = move;
    }

    if (score > alpha) {
      alpha = score;
      entryType = EntryType.EXACT;
    }

    // Beta cutoff
    if (alpha >= beta) {
      // Store killer move
      addKillerMove(move, depth, state);
      entryType = EntryType.LOWER_BOUND;
      break;
    }
  }

  // Store in transposition table
  state.transpositionTable.store(snapshot, {
    depth,
    score: bestScore,
    type: entryType,
    bestMove
  });

  return bestScore;
}

/**
 * Quiescence search - continue searching captures/threats to avoid horizon effect
 */
function quiescenceSearch(
  snapshot: GameStateSnapshot,
  map: GameMap,
  alpha: number,
  beta: number,
  state: SearchState,
  maxDepth: number
): number {
  state.nodesSearched++;

  // Stand-pat score
  const standPat = evaluate(snapshot, map);

  if (maxDepth <= 0) {
    return standPat;
  }

  if (standPat >= beta) {
    return beta;
  }

  if (standPat > alpha) {
    alpha = standPat;
  }

  const activeUnits = getActiveUnits(snapshot, snapshot.currentFaction);

  // Only consider tactical moves (attacks, captures)
  let tacticalMoves: GeneratedMove[] = [];
  for (const unit of activeUnits) {
    const unitMoves = generateMovesForUnit(snapshot, unit, map);
    // Filter to only attacks and moves that capture cities
    const tactical = unitMoves.filter(m =>
      m.flags.isKillingBlow ||
      m.flags.capturesCity ||
      m.targetId !== undefined  // Any attack
    );
    tacticalMoves.push(...tactical);
  }

  if (tacticalMoves.length === 0) {
    return standPat;
  }

  tacticalMoves = orderMoves(tacticalMoves);
  tacticalMoves = tacticalMoves.slice(0, 10);  // Limit quiescence moves

  for (const move of tacticalMoves) {
    const newSnapshot = applyMove(snapshot, move);
    const score = -quiescenceSearch(
      switchPerspective(newSnapshot),
      map,
      -beta,
      -alpha,
      state,
      maxDepth - 1
    );

    if (score >= beta) {
      return beta;
    }

    if (score > alpha) {
      alpha = score;
    }
  }

  return alpha;
}

/**
 * Check if search time is up
 */
function isTimeUp(state: SearchState): boolean {
  return (Date.now() - state.startTime) >= state.timeLimit;
}

/**
 * Add a killer move for the given depth
 */
function addKillerMove(move: GeneratedMove, depth: number, state: SearchState): void {
  let killers = state.killerMoves.get(depth);
  if (!killers) {
    killers = [];
    state.killerMoves.set(depth, killers);
  }

  // Don't add duplicates
  if (killers.some(k => k.unitId === move.unitId && k.to?.x === move.to?.x && k.to?.y === move.to?.y)) {
    return;
  }

  // Keep only 2 killer moves per depth
  killers.unshift(move);
  if (killers.length > 2) {
    killers.pop();
  }
}

/**
 * Apply killer move ordering - prioritize moves that caused cutoffs at this depth
 */
function applyKillerMoveOrdering(moves: GeneratedMove[], depth: number, state: SearchState): GeneratedMove[] {
  const killers = state.killerMoves.get(depth);
  if (!killers || killers.length === 0) {
    return moves;
  }

  const result: GeneratedMove[] = [];
  const remaining: GeneratedMove[] = [];

  for (const move of moves) {
    const isKiller = killers.some(k =>
      k.unitId === move.unitId &&
      k.to?.x === move.to?.x &&
      k.to?.y === move.to?.y &&
      k.type === move.type
    );

    if (isKiller) {
      result.push(move);
    } else {
      remaining.push(move);
    }
  }

  return [...result, ...remaining];
}

/**
 * Update history table for successful moves
 */
function updateHistory(move: GeneratedMove, depth: number, state: SearchState): void {
  const key = getMoveKey(move);
  const current = state.historyTable.get(key) || 0;
  state.historyTable.set(key, current + depth * depth);  // Weight by depth squared
}

/**
 * Apply history heuristic ordering
 */
function applyHistoryOrdering(moves: GeneratedMove[], state: SearchState): GeneratedMove[] {
  return moves.sort((a, b) => {
    const scoreA = state.historyTable.get(getMoveKey(a)) || 0;
    const scoreB = state.historyTable.get(getMoveKey(b)) || 0;
    return scoreB - scoreA;  // Higher history score first
  });
}

/**
 * Get a unique key for a move (for history table)
 */
function getMoveKey(move: GeneratedMove): string {
  return `${move.unitId}:${move.type}:${move.to?.x ?? ''},${move.to?.y ?? ''}:${move.targetId ?? ''}`;
}

/**
 * Evaluate all moves and return them sorted by evaluation score
 * Used when minimax is too slow
 */
export function evaluateAndSortMoves(
  snapshot: GameStateSnapshot,
  moves: GeneratedMove[],
  _map: GameMap  // Reserved for terrain-aware evaluation
): Array<{ move: GeneratedMove; score: number }> {
  const evaluated = moves.map(move => {
    const newSnapshot = applyMove(snapshot, move);
    const score = quickEvaluate(newSnapshot);
    return { move, score };
  });

  return evaluated.sort((a, b) => b.score - a.score);
}
